// Court letter templates — Time-Served Fine Waiver + Warrant Recall.
//
// Both letters live as text templates the staff sends to municipal courts
// on behalf of NJRC participants. We generate them client-side, fill in
// what we know, leave clear bracketed placeholders for what we don't,
// and copy the result to the clipboard in BOTH HTML (bold-preserving)
// and plain-text formats so Word / Google Docs picks up the formatting.
//
// What we auto-fill:
//   - Today's date
//   - Participant full name + DOB
//   - Case numbers (filtered per letter type — balance>0 for fine
//     waiver, hasWarrant=true for warrant)
//   - Total fine balance (fine waiver only)
//   - Address / phone / email — from petitioner-details when present
//
// What we leave bracketed:
//   - Court administrator name + address (no per-court rolodex yet)
//   - Pronouns (no gender field at intake yet)
//
// Bracketed text is wrapped in <strong> in the HTML version so the
// staffer can spot every fill-in field at a glance once it's in Word.

import type { OpenCase } from "../pages/ClientSummaryPage";

export type LetterKind = "fineWaiver" | "warrant";

export interface LetterInputs {
  /** Job — drives name, DOB. */
  fullName: string;
  dob: string;        // YYYY-MM-DD
  /** Open cases (already filtered by relevance per letter type by caller). */
  cases: OpenCase[];
  /** Sum of balance-due across the cases included. */
  totalFines: number;
  /** Optional contact info from PetitionerDetails — use what we have,
   *  bracket what we don't. */
  participantStreet?: string;
  participantCityStateZip?: string;
  participantPhone?: string;
  participantEmail?: string;
  /** Court name + cases-per-court grouping context. */
  courtName: string | null;
  /** Court address + administrator from the NJ municipal court directory.
   *  When provided, the preview HTML un-brackets these fields so it
   *  matches what the .docx download will look like. */
  courtAddressLines?: string[];
  courtAdminName?: string;
  courtAdminLastName?: string;
  courtAdminTitle?: "Mr." | "Ms.";
}

export interface LetterOutput {
  /** HTML form — for clipboard `text/html`. Word + Docs render bold. */
  html: string;
  /** Plain text form — for clipboard `text/plain`. */
  plain: string;
  /** Suggested filename if the staffer wants to save. */
  filename: string;
}

// ─── Public entry ─────────────────────────────────────────────────────

export function generateLetter(kind: LetterKind, input: LetterInputs): LetterOutput {
  const ctx = buildContext(input);
  if (kind === "fineWaiver") return buildFineWaiver(ctx);
  return buildWarrant(ctx);
}

// ─── Letter title (for the modal heading + filename) ─────────────────

export function letterTitle(kind: LetterKind): string {
  return kind === "fineWaiver"
    ? "Legal Services Fine Waiver"
    : "Legal Services Warrant Letter";
}

// ─── Shared context ──────────────────────────────────────────────────

interface Context extends LetterInputs {
  todayLong: string;
  caseNumberLine: string;
  participantAddressLines: string[]; // pre-escaped HTML strings
  /** Pre-rendered HTML for each slot. Bracketed placeholders come back
   *  wrapped in <strong>; values resolved from data come back as plain
   *  escaped text (no <strong>) so they read as part of the prose.
   *  Templates just splice these into HTML without their own <strong>. */
  P: {
    courtAdminName: string;
    courtAdminLastName: string;
    addressBlock: string;       // 3-line address block, HTML with <br/>s
    phone: string;
    email: string;
    /** Participant pronouns — kept bracketed in the client preview
     *  (we don't pass participant gender to the client). The .docx
     *  download resolves these server-side from gender inference. */
    Mr: string;
    his: string;
    he: string;
    him: string;
    /** Court admin's title — resolved from courtAdminTitle when the
     *  directory lookup found a match; bracketed otherwise. */
    MrAdmin: string;
  };
}

/** Render a placeholder either as plain escaped text (resolved) or as
 *  <strong>BRACKETED</strong> (so it pops in Word). */
function slot(resolved: string | undefined, bracketed: string): string {
  if (resolved && resolved.trim().length > 0) return escapeHtml(resolved);
  return `<strong>${escapeHtml(bracketed)}</strong>`;
}

function buildContext(input: LetterInputs): Context {
  const today = new Date();
  const todayLong = today.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const caseNumberLine = input.cases
    .map((c) => c.caseNumber)
    .filter((n): n is string => Boolean(n))
    .join(", ");

  const participantAddressLines: string[] = [];
  if (input.participantStreet) participantAddressLines.push(input.participantStreet);
  if (input.participantCityStateZip) participantAddressLines.push(input.participantCityStateZip);

  // Court address block: when we have directory lines, render them as
  // plain escaped text. Otherwise fall back to the 3 bracketed slots.
  const addressBlock = input.courtAddressLines && input.courtAddressLines.length > 0
    ? input.courtAddressLines.map((l) => escapeHtml(l)).join("<br/>")
    : `<strong>[ADDRESS 1]</strong><br/><strong>[ADDRESS 2]</strong><br/><strong>[CITY, NJ ZIP]</strong>`;

  return {
    ...input,
    todayLong,
    caseNumberLine: caseNumberLine || "[LIST ALL TICKET / CASE NUMBERS]",
    participantAddressLines,
    P: {
      courtAdminName: slot(input.courtAdminName, "[COURT ADMINISTRATOR NAME]"),
      courtAdminLastName: slot(input.courtAdminLastName, "[COURT ADMINISTRATOR LAST NAME]"),
      addressBlock,
      phone: slot(undefined, "[MOBILE NUMBER]"),
      email: slot(undefined, "[EMAIL ADDRESS]"),
      Mr: `<strong>[MR. / MS.]</strong>`,
      his: `<strong>[HIS / HER]</strong>`,
      he: `<strong>[HE / SHE]</strong>`,
      him: `<strong>[HIM / HER]</strong>`,
      MrAdmin: slot(input.courtAdminTitle, "[MR. / MS.]"),
    },
  };
}

// ─── Fine waiver letter ───────────────────────────────────────────────

function buildFineWaiver(c: Context): LetterOutput {
  const fineDollars = formatMoney(c.totalFines);

  const html = wrapHtml([
    `<p><strong>${c.todayLong}</strong></p>`,
    `<p><strong>VIA EMAIL</strong></p>`,
    `<p>Attn: ${c.P.courtAdminName}, Court Administrator<br/>` +
      `<strong>${courtNameLine(c)}</strong><br/>` +
      `${c.P.addressBlock}</p>`,
    `<p><strong>Re: ${c.fullName} (${escapeHtml(c.caseNumberLine)})</strong></p>`,
    `<p>Dear ${c.P.MrAdmin} ${c.P.courtAdminLastName}:</p>`,
    `<p>New Jersey Reentry Corporation (NJRC) respectfully submits this letter regarding ` +
      `${c.P.Mr} ${c.fullName}'s open matters. NJRC is a non-profit agency ` +
      `pursuing a social mission to remove barriers to employment for formerly incarcerated ` +
      `individuals reentering society. NJRC is not a law firm, does not provide legal advice, ` +
      `and does not act as counsel to its program participants in any forum. To receive NJRC's ` +
      `full range of services and benefits, participants are required to 1) attend (in-person ` +
      `or by Zoom) a four-day orientation program, 2) remain engaged with NJRC staff (which ` +
      `includes a case manager, legal services coordinator, employment specialist, and licensed ` +
      `social worker), and 3) otherwise comply with NJRC guidelines. ` +
      `${c.P.Mr} ${escapeHtml(lastName(c.fullName))} has done all of these things.</p>`,
    `<p>${c.P.Mr} ${escapeHtml(lastName(c.fullName))}'s contact information is below:</p>`,
    contactBlockHtml(c),
    `<p>${c.P.Mr} ${escapeHtml(lastName(c.fullName))} enrolled with NJRC seeking ` +
      `help with employment, general health and wellness, and restoration of ${c.P.his} ` +
      `driving privileges (among other things). Unfortunately, ${c.P.Mr} ` +
      `${escapeHtml(lastName(c.fullName))}'s open matters carry a <strong>${escapeHtml(fineDollars)}</strong> ` +
      `balance and may have triggered Driver License suspensions, all of which is frustrating ` +
      `${c.P.his} reentry.</p>`,
    `<p>${c.P.Mr} ${escapeHtml(lastName(c.fullName))} qualified to enroll in NJRC's ` +
      `program because ${c.P.he} is a formerly incarcerated individual reentering ` +
      `${c.P.his} community ` +
      `<strong>(see attached — evidence of incarceration: PROMIS/Gavel, Judgment of Conviction, DOC sheet, etc.)</strong>. ` +
      `In light of ${c.P.his} incarceration and other mitigating factors, ` +
      `${c.P.Mr} ${escapeHtml(lastName(c.fullName))} respectfully requests that the ` +
      `Court grant ${c.P.him} Time Served Credit for ${c.P.his} fines, ` +
      `and vacate the matter in the interests of justice.</p>`,
    closingParagraphHtml(c),
    signatureBlockHtml(c),
  ]);

  const plain = stripHtml(html);
  return { html, plain, filename: filenameFor("fine-waiver", c.fullName) };
}

// ─── Warrant recall letter ────────────────────────────────────────────

function buildWarrant(c: Context): LetterOutput {
  const html = wrapHtml([
    `<p><strong>${c.todayLong}</strong></p>`,
    `<p><strong>VIA EMAIL</strong></p>`,
    `<p>Attn: ${c.P.courtAdminName}, Court Administrator<br/>` +
      `<strong>${courtNameLine(c)}</strong><br/>` +
      `${c.P.addressBlock}</p>`,
    `<p><strong>Re: ${c.fullName} (${escapeHtml(c.caseNumberLine)})</strong></p>`,
    `<p>Dear ${c.P.MrAdmin} ${c.P.courtAdminLastName}:</p>`,
    `<p>New Jersey Reentry Corporation (NJRC) respectfully submits this letter regarding ` +
      `${c.P.Mr} ${c.fullName}'s warrant. NJRC is a non-profit agency pursuing ` +
      `a social mission to remove barriers to employment for formerly incarcerated individuals ` +
      `reentering society. NJRC is not a law firm, does not provide legal advice, and does not ` +
      `act as counsel to its program participants in any forum. To receive NJRC's full range of ` +
      `services and benefits, participants are required to 1) attend (in-person or by Zoom) a ` +
      `four-day orientation program, 2) remain engaged with NJRC staff (which includes a case ` +
      `manager, legal services coordinator, employment specialist, and licensed social worker), ` +
      `and 3) otherwise comply with NJRC guidelines. ${c.P.Mr} ` +
      `${escapeHtml(lastName(c.fullName))} has done all of these things.</p>`,
    `<p>${c.P.Mr} ${escapeHtml(lastName(c.fullName))}'s contact information is below:</p>`,
    contactBlockHtml(c),
    `<p>${c.P.Mr} ${escapeHtml(lastName(c.fullName))} enrolled with NJRC seeking ` +
      `help with employment, general health and wellness, and restoration of ${c.P.his} ` +
      `driving privileges (among other things). Unfortunately, ${c.P.Mr} ` +
      `${escapeHtml(lastName(c.fullName))}'s open matters feature an active warrant and may have ` +
      `triggered Driver License suspensions, all of which is frustrating ${c.P.his} reentry.</p>`,
    `<p>${c.P.Mr} ${escapeHtml(lastName(c.fullName))} respectfully requests that the ` +
      `Court recall the warrant and set an expedited hearing date so that ${c.P.he} ` +
      `has the opportunity to resolve the matter as soon as possible. ${c.P.Mr} ` +
      `${escapeHtml(lastName(c.fullName))} has given assurances that, if the hearing is virtual, ` +
      `${c.P.he} will appear from our offices.</p>`,
    closingParagraphHtml(c),
    signatureBlockHtml(c),
  ]);

  const plain = stripHtml(html);
  return { html, plain, filename: filenameFor("warrant-recall", c.fullName) };
}

// ─── Shared paragraph builders ───────────────────────────────────────

function contactBlockHtml(c: Context): string {
  // PARTICIPANT address (not court). Use petitioner-details when present;
  // otherwise show the bracketed [ADDRESS 1] / [ADDRESS 2] / [CITY, NJ ZIP]
  // placeholders (independent of the court address-block field above).
  const addr =
    c.participantAddressLines.length > 0
      ? c.participantAddressLines.map((l) => escapeHtml(l)).join("<br/>")
      : `<strong>[ADDRESS 1]</strong><br/>` +
        `<strong>[ADDRESS 2]</strong><br/>` +
        `<strong>[CITY, NJ ZIP]</strong>`;
  const phone = c.participantPhone
    ? escapeHtml(c.participantPhone)
    : `${c.P.phone}`;
  const email = c.participantEmail
    ? escapeHtml(c.participantEmail)
    : `${c.P.email}`;
  return (
    `<p>${addr}<br/>` +
    `${phone}<br/>` +
    `DOB: ${escapeHtml(formatDob(c.dob))}<br/>` +
    `${email}</p>`
  );
}

function closingParagraphHtml(c: Context): string {
  return (
    `<p>${c.P.Mr} ${escapeHtml(lastName(c.fullName))} communicates with this office ` +
    `frequently — any correspondence sent to NJRC's Corporate Headquarters by email ` +
    `(<strong>dwatson@njreentry.org</strong>) or fax (<strong>201.604.7846</strong>) will be relayed ` +
    `promptly. NJRC hopes that this letter demonstrates that ${c.P.Mr} ` +
    `${escapeHtml(lastName(c.fullName))} has set ${c.P.his} life on a positive and ` +
    `productive trajectory and is committed to changing ${c.P.his} previous ` +
    `circumstances. We thank the Court in advance for its time and consideration and remain ` +
    `available at the Court's convenience to respond to any questions or concerns.</p>`
  );
}

function signatureBlockHtml(c: Context): string {
  return (
    `<p>Sincerely (electronically signed by),</p>` +
    `<p><strong>s/Damon Christian Watson/s</strong>, Senior Director of Legal Services &amp; Corporate Development</p>` +
    `<p>Cc: ${c.fullName} (by email)</p>`
  );
}

function courtNameLine(c: Context): string {
  return c.courtName ?? "[COURT NAME]";
}

// ─── HTML wrapper + escapers ──────────────────────────────────────────

/** Wrap the paragraph HTML chunks in a minimal document with serif body
 *  so the rendered preview matches what Word will produce. */
function wrapHtml(paragraphs: string[]): string {
  return [
    `<div style="font-family: 'Times New Roman', Times, serif; font-size: 12pt; line-height: 1.5; color: #111;">`,
    paragraphs.join("\n"),
    `</div>`,
  ].join("\n");
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Strip HTML tags + collapse double spaces. Used for the plain-text
 *  clipboard payload — pasting into a plain-text editor or terminal
 *  should still read sensibly. */
function stripHtml(html: string): string {
  return html
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/?(?:strong|b|em|i|div|span)[^>]*>/gi, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// ─── Small helpers ────────────────────────────────────────────────────

function lastName(full: string): string {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  return parts[parts.length - 1] ?? full;
}

function formatMoney(n: number): string {
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDob(iso: string): string {
  // Accept YYYY-MM-DD or fall through.
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  const d = new Date(`${m[1]}-${m[2]}-${m[3]}T12:00:00`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

function filenameFor(slug: string, fullName: string): string {
  const safe = fullName.replace(/[^A-Za-z]+/g, "_").replace(/^_+|_+$/g, "");
  return `NJRC_${slug}_${safe}.txt`;
}

// ─── Clipboard write (HTML + plain) ──────────────────────────────────

/** Write both HTML + plain text to the clipboard so Word picks up bold
 *  formatting while terminal / plain-text targets get a readable
 *  fallback. Returns true on success. */
export async function copyLetterToClipboard(letter: LetterOutput): Promise<boolean> {
  // Modern path — Clipboard API with multiple MIME types. Required for
  // Word to recognize the rich-text payload.
  if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
    try {
      const item = new ClipboardItem({
        "text/html": new Blob([letter.html], { type: "text/html" }),
        "text/plain": new Blob([letter.plain], { type: "text/plain" }),
      });
      await navigator.clipboard.write([item]);
      return true;
    } catch {
      // fall through to plain-text fallback
    }
  }
  // Fallback: plain text only. Word will paste without bold formatting
  // but the staffer can still apply it manually.
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(letter.plain);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}
